import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import DatePickerSingle from 'components/date-picker/date-picker-single/DatePickerSingleLazy';
import CroppedProfileImage from 'components/cropped-profile-image/CroppedProfileImage';
import SearchableDropdown from 'components/inputs/searchable-dropdown/SearchableDropdown';

import UnderageLegalWarning from '../../../legal-warning/UnderageLegalWarning';

import { uploadImage } from 'modules/influencers/reducers/influencerImages';

import { __t, applyTranslations } from 'utils/language';
import { SHORT_STRING_MAX_LENGTH, isArrayRequired, isBooleanRequired, isRequired, isUrl, maxLength } from 'utils/validators';
import { HTML_DATE_FORMAT, MIN_USER_AGE, getHTMLDate } from 'utils/date';
import { GENDER_OPTIONS } from 'utils/influencers';
import { wrappedCitySearchToDropdownItems } from 'utils/locations';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./fields-general.i18n.json'));

import type {
  Agency,
  Data,
  Image,
  InfluencerProfileForm,
  LocationGeo,
  MixedSelectDropdownItem,
  TextEntry
} from 'types';

type OwnProps = {
  isOnCreateStep?: boolean,
  managedByAgency?: boolean,
  agencyId?: number,
  details: InfluencerProfileForm,
  onChange: (details: InfluencerProfileForm) => void,
  onInputFieldDescriptionShow?: (event: React.SyntheticEvent<HTMLInputElement | HTMLOptionElement>) => void,
  onFieldDescriptionHide?: () => void
};

type StateProps = {
  profileImage: Image | undefined | null
};

type DispatchProps = {
  dispatchUploadImage: (c: InfluencerProfileForm, b: string, a: Image) => Promise<Image>,
  dispatchSearchCities: (a: string) => Promise<Array<MixedSelectDropdownItem>>
};

type Props = OwnProps & StateProps & DispatchProps;

const IS_AGENCY_MANAGED_OPTIONS = ['yes', 'no'];
const COMMON_MSG_KEY = 'collabary_common';

function mapGeoLocationToMixedDropdown(location: LocationGeo): MixedSelectDropdownItem {
  const fullName = `${location.city}, ${location.state}, ${location.country}`;

  return {
    identifier: fullName,
    isDisabled: false,
    isFocused: false,
    isSelected: true,
    label: fullName,
    labelWhenSelected: location.city,
    data: location,
    value: fullName
  };
}

class ProfileFormFieldsGeneral extends React.PureComponent<Props> {
  componentWillUnmount() {
    const { onFieldDescriptionHide } = this.props;

    if (onFieldDescriptionHide) {
      onFieldDescriptionHide();
    }
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { details } = this.props;

    return [...GENDER_OPTIONS].map(value => ({
      identifier: value,
      isDisabled: false,
      isSelected: details.influencerGender === value,
      label: __t(`${COMMON_MSG_KEY}.gender.${value.toLowerCase()}`),
      value
    }));
  };

  handleProfileImageChanged = (image: Image) => {
    const { dispatchUploadImage, details } = this.props;

    return dispatchUploadImage(details, 'profile', image);
  };

  handleDateChange = (date?: string | null): void => {
    const { details, onChange } = this.props;
    const formattedDate = getHTMLDate(date);

    onChange({
      ...details,
      influencerDateOfBirth: formattedDate
    });
  };

  handleFieldChanged = (event: React.SyntheticEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { details, onChange } = this.props;

    const target = event.currentTarget;
    let targetValue = target.value;
    const targetName = target.name;

    if (targetName === 'influencerDateOfBirth') {
      targetValue = getHTMLDate(targetValue) ?? '';
    }

    onChange({
      ...details,
      [targetName]: targetValue
    });
  };

  handleGenderChanged = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { details, onChange } = this.props;

    onChange({
      ...details,
      influencerGender: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  handleAgencyFieldChanged = (event: React.SyntheticEvent<TextEntry>): void => {
    const { details, onChange } = this.props;

    const target = event.currentTarget;
    const targetValue = target.value;
    const targetName = target.name.replace('OfAgency', '');

    const agencyDetails = {
      ...details.agencyDetails,
      [targetName]: targetValue
    } as Agency;

    onChange({
      ...details,
      agencyDetails
    });
  };

  handleIsAgencyManagedChanged = (event: React.SyntheticEvent<TextEntry>): void => {
    const { details, onChange } = this.props;

    const target = event.currentTarget;
    const targetValue = target.value;
    const isAgencyManaged = targetValue === IS_AGENCY_MANAGED_OPTIONS[0];

    onChange({
      ...details,
      agencyDetails: isAgencyManaged ? details.agencyDetails : null,
      isAgencyManaged
    });
  };

  handleLocationsChanged = (locations: Array<MixedSelectDropdownItem>): void => {
    const { details, onChange } = this.props;

    onChange({
      ...details,
      locations: locations
        .filter(item => item.isSelected)
        .map(({ data = {} }) => ({
          city: data.city,
          country: data.country,
          countryCode: data.countryCode,
          state: data.state
        }))
    });
  };

  renderAgeWarning(): React.ReactNode {
    const { details } = this.props;

    return (
      <UnderageLegalWarning dateOfBirth={details.influencerDateOfBirth} />
    );
  }

  renderAgencyManagedFields() {
    const { onInputFieldDescriptionShow, onFieldDescriptionHide, details, agencyId } = this.props;
    const hasStaticTooltips = !onFieldDescriptionHide;
    const agencyDetailsName = details.agencyDetails && details.agencyDetails.name || '';
    const agencyDetailsWebsite = details.agencyDetails && details.agencyDetails.url || '';


    if (!details.isAgencyManaged || agencyId) {
      return null;
    }

    return (
      <React.Fragment>
        <FormField
          direction="column"
          showRequiredMark
          showValidationIcon={false}
          label={__t('fields_general.fields.agency_details.name')}
          labelType="highlighted"
          tooltipMessage={hasStaticTooltips ? __t('fields_general.field_descriptions.name_of_agency') : ''}
          validators={[
            isRequired(details.isAgencyManaged ? agencyDetailsName : 'ok'),
            maxLength(agencyDetailsName, SHORT_STRING_MAX_LENGTH)
          ]}
        >
          <Input
            value={agencyDetailsName}
            name="nameOfAgency"
            width="responsive"
            onChange={this.handleAgencyFieldChanged}
            onFocus={onInputFieldDescriptionShow}
            onBlur={onFieldDescriptionHide}
          />
        </FormField>
        <FormField
          direction="column"
          showRequiredMark
          showValidationIcon={false}
          label={__t('fields_general.fields.agency_details.url')}
          labelType="highlighted"
          validators={[
            isRequired(details.isAgencyManaged ? agencyDetailsWebsite : 'ok'),
            isUrl(agencyDetailsWebsite)
          ]}
        >
          <Input
            value={agencyDetailsWebsite}
            name="urlOfAgency"
            width="responsive"
            onChange={this.handleAgencyFieldChanged}
          />
        </FormField>
      </React.Fragment>
    );
  }

  render() {
    const {
      details,
      profileImage,
      isOnCreateStep,
      managedByAgency,
      agencyId,
      onInputFieldDescriptionShow,
      onFieldDescriptionHide,
      dispatchSearchCities
    } = this.props;
    const hasStaticTooltips = !onFieldDescriptionHide;
    const locations = details.locations;

    return (
      <section
        className="profile-form-fields"
        data-qa="profile-form-fields-general"
      >
        <div className="profile-form-fields__profile-image">
          <FormField
            direction="column"
            showValidationIcon={false}
            validators={[
              isRequired(profileImage ? profileImage.url : '')
            ]}
          >
            <CroppedProfileImage
              image={profileImage}
              canEdit
              type="profile"
              onChange={this.handleProfileImageChanged}
            />
          </FormField>
        </div>
        <div className="profile-form-fields__columns">
          <div className="profile-form-fields__column profile-form-fields__column--name">
            <FormField
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              label={__t('fields_general.fields.influencer_first_name')}
              labelType="highlighted"
              validators={[
                isRequired(details.influencerFirstName),
                maxLength(details.influencerFirstName, SHORT_STRING_MAX_LENGTH)
              ]}
            >
              <Input
                value={details.influencerFirstName || ''}
                name="influencerFirstName"
                width="responsive"
                onChange={this.handleFieldChanged}
                onFocus={onInputFieldDescriptionShow}
                onBlur={onFieldDescriptionHide}
              />
            </FormField>
          </div>
          <div className="profile-form-fields__column profile-form-fields__column--name">
            <FormField
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              label={__t('fields_general.fields.influencer_last_name')}
              labelType="highlighted"
              validators={[
                isRequired(details.influencerLastName),
                maxLength(details.influencerLastName, SHORT_STRING_MAX_LENGTH)
              ]}
            >
              <Input
                value={details.influencerLastName || ''}
                name="influencerLastName"
                width="responsive"
                onChange={this.handleFieldChanged}
                onFocus={onInputFieldDescriptionShow}
                onBlur={onFieldDescriptionHide}
              />
            </FormField>
          </div>
        </div>
        <FormField
          direction="column"
          showRequiredMark={false}
          showValidationIcon={false}
          label={__t('fields_general.fields.artist_name')}
          labelType="highlighted"
          tooltipMessage={hasStaticTooltips ? __t('fields_general.field_descriptions.artist_name') : ''}
          validators={[
            maxLength(details.artistName, SHORT_STRING_MAX_LENGTH)
          ]}
        >
          <Input
            value={details.artistName || ''}
            name="artistName"
            width="responsive"
            onChange={this.handleFieldChanged}
            onFocus={onInputFieldDescriptionShow}
            onBlur={onFieldDescriptionHide}
          />
        </FormField>
        <div className="profile-form-fields__columns">
          <div className="profile-form-fields__column profile-form-fields__column--dob">
            <FormField
              label={__t('fields_general.fields.influencer_date_of_birth')}
              labelType="highlighted"
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isRequired(
                  details.influencerDateOfBirth,
                  __t('fields_general.errors.date_of_birth_error')
                )
              ]}
            >
              <DatePickerSingle
                name="influencerDateOfBirth"
                date={details.influencerDateOfBirth}
                maxAllowableDate={moment().subtract(MIN_USER_AGE, 'y').format(HTML_DATE_FORMAT)}
                onChange={this.handleDateChange}
              />
            </FormField>
          </div>
          <div className="profile-form-fields__column profile-form-fields__column--location">
            <FormField
              label={__t('fields_general.fields.locations')}
              labelType="highlighted"
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isArrayRequired(locations)
              ]}
            >
              <SearchableDropdown
                withRandomizedAutocomplete={isOnCreateStep}
                key={JSON.stringify(locations)}
                items={locations.map(mapGeoLocationToMixedDropdown)}
                dataFetcher={dispatchSearchCities}
                placeholder={__t('fields_general.fields.locations_placeholder')}
                onItemsUpdate={this.handleLocationsChanged}
              />
            </FormField>
          </div>
          {this.renderAgeWarning()}
        </div>
        {
          !managedByAgency && !agencyId && (
            <FormField
              label={__t('fields_general.fields.is_agency_managed.is_managed')}
              labelType="highlighted"
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isBooleanRequired(details.isAgencyManaged)
              ]}
            >
              {
            IS_AGENCY_MANAGED_OPTIONS.map(option => (
              <InputRadio
                name="isAgencyManaged"
                id={`is-agency-managed-${option}`}
                key={`is-agency-managed-${option}`}
                value={option}
                checked={option === IS_AGENCY_MANAGED_OPTIONS[0] && details.isAgencyManaged || option === IS_AGENCY_MANAGED_OPTIONS[1] && details.isAgencyManaged === false}
                label={__t(`fields_general.fields.is_agency_managed.${option}`)}
                onChange={this.handleIsAgencyManagedChanged}
              />
            ))
          }
            </FormField>
          )
        }
        {this.renderAgencyManagedFields()}
      </section>
    );
  }
}

const mapStateToProps = ({ influencerImages }): StateProps => ({
  profileImage: influencerImages.profileImage
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUploadImage: (influencerProfile, type, image) => dispatch(uploadImage(influencerProfile, type, image)),
  dispatchSearchCities: wrappedCitySearchToDropdownItems(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileFormFieldsGeneral);
